import { z } from 'zod';

import { ReportColumn, ReportSortBy, ReportTransactionRole, TransactionType } from './common';
import { ReportFormat } from './report';
import { DateStringSchema, EmailStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { BookingStatus, ItemType } from '../booking/booking';
import { makeEndpoint, paginateResponseSchema, stringBooleanSchema } from '../common';

export const GuestReportColumn = [
  ReportColumn.EXPERIENCE_DATE,
  ReportColumn.TRANSACTION_DATE,
  ReportColumn.FIRST_NAME,
  ReportColumn.LAST_NAME,
  ReportColumn.EMAIL,
  ReportColumn.PHONE,
  ReportColumn.CONFIRMATION_CODE,
  ReportColumn.EXPERIENCE_NAME,
  ReportColumn.START_TIME,
  ReportColumn.END_TIME,
  ReportColumn.PRICING_LABEL,

  ReportColumn.ITEM_TYPE,
  ReportColumn.TRANSACTION_ROLE,
  ReportColumn.PARTNER_NAME,
  ReportColumn.TOTAL,
  ReportColumn.TOTAL_PAID,
  ReportColumn.REFUNDS,
  ReportColumn.BOOKING_STATUS,
  ReportColumn.CATEGORY,
  ReportColumn.CART_ID,
  ReportColumn.CHECK_IN_STATUS,

  ReportColumn.HOST,
  ReportColumn.PAYMENT_METHOD,
  ReportColumn.ACCOUNT,
  ReportColumn.NOTES,
  ReportColumn.INTERNAL_NOTES,
  ReportColumn.CUSTOM_QUESTION_ANSWERS,
] as const satisfies ReadonlyArray<ReportColumn>;
export type GuestReportColumnType = (typeof GuestReportColumn)[number];

export const guestReportsColumnsMap = {
  [ReportColumn.EXPERIENCE_DATE]: 'Experience Date',
  [ReportColumn.TRANSACTION_DATE]: 'Transaction Date UTC',
  [ReportColumn.FIRST_NAME]: 'First Name',
  [ReportColumn.LAST_NAME]: 'Last Name',
  [ReportColumn.EMAIL]: 'Email',
  [ReportColumn.PHONE]: 'Phone',
  [ReportColumn.CONFIRMATION_CODE]: 'Confirmation Code',
  [ReportColumn.EXPERIENCE_NAME]: 'Experience',
  [ReportColumn.START_TIME]: 'Start Time',
  [ReportColumn.END_TIME]: 'End Time',
  [ReportColumn.PRICING_LABEL]: 'Pricing Label',

  [ReportColumn.ITEM_TYPE]: 'Item Type',
  [ReportColumn.TRANSACTION_ROLE]: 'Transaction Role',
  [ReportColumn.PARTNER_NAME]: 'Partner Name',
  [ReportColumn.TOTAL]: 'Item Price',
  [ReportColumn.TOTAL_PAID]: 'Total Paid',
  [ReportColumn.REFUNDS]: 'Refunds',
  [ReportColumn.BOOKING_STATUS]: 'Booking Status',
  [ReportColumn.CATEGORY]: 'Category',
  [ReportColumn.CART_ID]: 'Cart ID',
  [ReportColumn.CHECK_IN_STATUS]: 'Check In Status',

  [ReportColumn.HOST]: 'Host',
  [ReportColumn.PAYMENT_METHOD]: 'Payment Method',
  [ReportColumn.ACCOUNT]: 'Account',
  [ReportColumn.NOTES]: 'Guest Notes',
  [ReportColumn.INTERNAL_NOTES]: 'Internal Notes',
  [ReportColumn.CUSTOM_QUESTION_ANSWERS]: 'Custom Question Answers',
} as const satisfies Record<GuestReportColumnType, string>;

export const GuestReportSortBy = [
  ReportSortBy.START_DATE_TIME,
  ReportSortBy.BOOKED_AT,
] as const satisfies ReadonlyArray<ReportSortBy>;

export enum GuestReportBookingSourceFilter {
  OWN = 'own',
  PROVIDED = 'provided',
  PROMOTED = 'promoted',
}

const baseGuestReportFilters = {
  from: DateStringSchema.optional(),
  to: DateStringSchema.optional(),
  experienceIds: z.array(z.string().uuid()).optional(),
  collectionIds: z.array(z.string().uuid()).optional(),
  eventId: z.string().uuid().optional(),
  categoryIds: z.array(z.string().uuid()).optional(),
  hostIds: z.array(z.string().uuid()).optional(),
  bookingStatus: z.array(z.nativeEnum(BookingStatus)).optional(),
  itemType: z.array(z.nativeEnum(ItemType)).optional(),
  confirmationCode: z.string().optional(),
  cartId: z.string().optional(),
  email: z.string().optional(),
  lastName: z.string().optional(),
  showNegativeRefundValues: stringBooleanSchema.optional(),
  transactionType: z.nativeEnum(TransactionType).optional().nullable(),
  bookingSources: z.array(z.nativeEnum(GuestReportBookingSourceFilter)).optional(),
  debug: stringBooleanSchema.optional(),
};

export type BaseGuestReportFiltersKeys = keyof typeof baseGuestReportFilters;

const guestReportFiltersSchema = z.object(baseGuestReportFilters);

export type GuestReportFilters = z.infer<typeof guestReportFiltersSchema>;

export const GuestReportRequestSchema = z.object({
  params: guestReportFiltersSchema,
  sortBy: z.enum(GuestReportSortBy),

  columns: z.array(z.enum(GuestReportColumn)),
  brandId: z.string().uuid().optional(),
  format: z.enum([ReportFormat.CSV, ReportFormat.PDF]).optional(),
});

export type GuestReportRequest = z.infer<typeof GuestReportRequestSchema>;

const PreviewGuestReportRequestSchema = guestReportFiltersSchema.extend({
  limit: z.coerce.number().optional(),
  page: z.coerce.number().optional(),
  sortBy: z.enum(GuestReportSortBy),
});

const guestReportItemSchema = z.object({
  experienceDate: z.string(),
  transactionDate: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string(),
  confirmationCode: z.string(),
  experienceName: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  pricingLabel: z.string(),
  itemType: z.string(),
  transactionRole: z.nativeEnum(ReportTransactionRole).or(z.literal('')),
  partnerName: z.string(),
  total: z.number(),
  totalPaid: z.number(),
  refunds: z.number().or(z.literal('')),
  bookingStatus: z.string(),
  category: z.string(),
  cartId: z.string(),
  checkInStatus: z.string(),
  hostName: z.string(),
  paymentMethod: z.string(),
  accountNumber: z.string(),
  notes: z.string(),
  internalNotes: z.string(),
  customQuestionAnswer: z.record(z.unknown()),
});

export type GuestReportItem = z.infer<typeof guestReportItemSchema>;

export const PreviewGuestReportEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof PreviewGuestReportRequestSchema>; brandId: string }) =>
    `v1/brands/${brandId}/reports/guests?${buildQueryString(query)}`,
  responseSchema: paginateResponseSchema(guestReportItemSchema),
  requestSchema: z.void(),
  querySchema: PreviewGuestReportRequestSchema,
});

export const GuestReportWithHeaderSchema = z.object({
  'Experience Date': z.string().optional(),
  'Transaction Date UTC': z.string().optional(),
  'First Name': z.string().optional(),
  'Last Name': z.string().optional(),
  Email: EmailStringSchema.optional(),
  Phone: z.string().optional(),
  'Confirmation Code': z.string().optional(),
  Experience: z.string().optional(),
  'Start Time': z.string().optional(),
  'End Time': z.string().optional(),
  'Pricing Label': z.string().optional(),

  'Item Type': z.string().optional(),
  'Transaction Role': z.string().optional(),
  'Partner Name': z.string().optional(),
  'Item Price': z.string().optional(),
  'Total Paid': z.string().optional(),
  Refunds: z.string().optional(),
  'Booking Status': z.string().optional(),
  Category: z.string().optional(),
  'Cart ID': z.string().optional(),
  'Check In Status': z.string().optional(),

  Host: z.string().optional(),
  'Guest Notes': z.string().optional(),
  'Internal Notes': z.string().optional(),
  'Payment Method': z.string().optional(),
  Account: z.string().optional(),
} satisfies Record<(typeof guestReportsColumnsMap)[Exclude<keyof typeof guestReportsColumnsMap, ReportColumn.CUSTOM_QUESTION_ANSWERS>], z.ZodType>);
export type GuestReportWithHeader = z.input<typeof GuestReportWithHeaderSchema>;
